import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { CONFIG } from '~/_shared/constants/config';
import { MapPageSplitScreenTab } from '~/map/page/splitScreen/mapPageSplitScreen.types';
import { clearSpreadsheetPreviewFilterState } from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import {
  userSettingsSplitPanelClose, userSettingsSplitPanelOpen,
  userSettingsSplitPanelSetTab,
} from '~/store/userData/settings/userDataSettings.actionCreators';
import { useSplitItemSettingsSelector } from '~/store/userData/settings/userDataSettings.selectors';

export const useSplitPanelState = (mapId: number | null) => {
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const splitItemSettings = useSplitItemSettingsSelector(clientId, mapId);
  const currentMapId = useMapIdSelector();

  const closePanel = useCallback(() => {
    if (mapId === null || clientId === null) {
      return;
    }

    dispatch(clearSpreadsheetPreviewFilterState());
    dispatch(userSettingsSplitPanelClose(clientId, mapId));
  }, [dispatch, clientId, mapId]);

  const openPanel = useCallback((defaultTab?: MapPageSplitScreenTab) => {
    if (mapId === null || clientId === null) {
      return;
    }

    dispatch(userSettingsSplitPanelOpen(clientId, mapId));
    if (defaultTab) {
      dispatch(userSettingsSplitPanelSetTab(clientId, mapId, defaultTab));
    }
  }, [dispatch, clientId, mapId]);

  const handleTabChange = useCallback((newTab: MapPageSplitScreenTab) => {
    if (!clientId || !mapId) {
      return;
    }
    dispatch(userSettingsSplitPanelSetTab(clientId, mapId, newTab));
  }, [clientId, dispatch, mapId]);

  const openPanelForCurrentMap = useCallback((defaultTab?: MapPageSplitScreenTab) => {
    if (currentMapId === null || clientId === null) {
      return;
    }

    dispatch(userSettingsSplitPanelOpen(clientId, currentMapId));
    if (defaultTab) {
      dispatch(userSettingsSplitPanelSetTab(clientId, currentMapId, defaultTab));
    }
  }, [dispatch, clientId, currentMapId]);

  const tab = CONFIG.ENABLE_CHARTS
    ? splitItemSettings.tab ?? MapPageSplitScreenTab.Spreadsheet
    : MapPageSplitScreenTab.Spreadsheet;

  const isActive = splitItemSettings.isActive;

  const getIsTabActive = useCallback((targetTab: MapPageSplitScreenTab) => {
    return isActive && tab === targetTab;
  }, [isActive, tab]);

  return useMemo(() => {
    return ({
      isActive,
      isTabActive: getIsTabActive,
      tab,
      openPanel,
      openPanelForCurrentMap,
      closePanel,
      getIsTabActive,
      onTabChange: handleTabChange,
    });
  }, [closePanel, getIsTabActive, handleTabChange, isActive, openPanel, openPanelForCurrentMap, tab]);
};
